import { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import tw from '@/styles/tailwind';
import api from '@/utils/api';

interface FutureFeeSelectorProps {
  communityId: number;
  unit: any;
  onFeesSelected: (fees: any[]) => void;
  mainColor?: string;
}

export default function FutureFeeSelector({
  communityId,
  unit,
  onFeesSelected,
  mainColor = 'secondary'
}: FutureFeeSelectorProps) {
  const [showFutureFees, setShowFutureFees] = useState(false);
  const [selectedFees, setSelectedFees] = useState([]);

  // Fetch future fees
  const { isLoading, data: futureFees } = useQuery({
    queryKey: ['community.future-fees', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees?future=1`).then(response => response.data),
    enabled: showFutureFees,
  });

  // Filtrar fees aplicables a esta unidad
  const applicableFees = useMemo(() => {
    if(!futureFees) return [];

    return futureFees.filter(fee => {
      // Solo mostrar main fees si la unidad tiene fee_type
      if(fee.type?.type === "main"){
        return unit.fee_type_id !== null;
      }
      return true;
    });
  }, [futureFees, unit]);

  function onPress(fee) {
    const feeIndex = selectedFees.findIndex(e => e.uuid === fee.uuid);

    let newFees;
    if(feeIndex === -1){
      newFees = [...selectedFees, fee];
    } else {
      newFees = selectedFees.filter(e => e.uuid !== fee.uuid);
    }

    setSelectedFees(newFees);
    onFeesSelected(newFees);
  }

  if (!showFutureFees) {
    return (
      <TouchableOpacity
        style={tw`mt-4 px-4`}
        onPress={() => setShowFutureFees(true)}
      >
        <Text style={tw`text-blue-500 underline text-sm`}>
          + Agregar pagos adelantados
        </Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={tw`mt-6`}>
      {isLoading && (
        <Text style={tw`px-4 text-gray-400 text-sm`}>
          Cargando cuotas futuras...
        </Text>
      )}

      {applicableFees.length > 0 && (
        <>
          <View style={tw`px-4 mb-3`}>
            <Text style={tw`text-gray-500 font-600`}>
              Cuotas Futuras
            </Text>
          </View>
          <View style={tw`gap-y-3`}>
            {applicableFees.map(fee => {
              const isSelected = !!selectedFees.find(e => e.uuid === fee.uuid);
              // Calcular el monto a mostrar
              const displayAmount = (fee.type?.type === "main" && unit.fee_type) ? unit.fee_type.amount : fee.amount;

              return (
                <TouchableOpacity
                  style={tw.style(`row bg-blue-50 rounded-xl px-4 py-2`, isSelected && `border border-${mainColor}`)}
                  key={fee.uuid}
                  onPress={() => onPress(fee)}
                >
                  <View style={tw`flex-1`}>
                    <View style={tw`row items-center`}>
                      <Text style={tw`text-${mainColor} font-600`}>{fee.name}</Text>
                      <View style={tw`ml-2 bg-blue-200 px-2 py-0.5 rounded`}>
                        <Text style={tw`text-blue-700 text-xs`}>Adelantado</Text>
                      </View>
                    </View>

                    <View style={tw`row`}>
                      <Text style={tw`text-red font-600`}>${displayAmount}</Text>
                    </View>
                  </View>

                  <View style={tw`h-5 w-5 rounded border border-gray-400 items-center justify-center`}>
                    {isSelected && (
                      <View style={tw`h-3.5 w-3.5 rounded bg-${mainColor}`}></View>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      )}

      {!isLoading && applicableFees.length === 0 && (
        <Text style={tw`px-4 text-gray-400 text-sm`}>
          No hay cuotas futuras disponibles
        </Text>
      )}
    </View>
  );
}
